/**
 * The middle of the proposal — the work, the choices, the extras, the total
 * and the signature.
 *
 * **This is PR 2's shell holding PR 1's body.** The visit card of
 * `docs/proposal.md` §6.1 — the count badge, the timing bar, the price said
 * the way it bills, the service rows and the detail levels — is PR 3, and the
 * option cards and compare grid of §7.3 are PR 4. What is here is the
 * behaviour PR 1 shipped, re-homed under the new sections so that every later
 * PR replaces a component rather than a page: the anchors, the section
 * headings and the brand variables are already what they will be.
 *
 * Each section carries the `id` its chip scrolls to (`section-nav.tsx`) and
 * nothing else knows those strings, which is why they are exported from here.
 */
import { useState } from 'react'
import {
  EMPTY_SIGNATURE, missingForAccept, visitIncluded,
  type MonthSplit, type ProposalPayment, type ProposalSelection,
  type ProposalView, type ProposalVisit, type SelectionTotals,
  type SignatureForm, type SignaturePayload, type SignedSummary,
} from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { formatDocumentDate, formatDollars, formatDollarsExact } from '../../lib/format.js'
import { MonthToggle, monthOffered, type MonthMode } from './month-toggle.js'
import { MonthBreakdown, splitOf } from './month-view.js'
import { SheetOrDialog } from '../../components/sheet-or-dialog.js'
import { ResponseLinks } from './responses.js'
import { priceLine } from './price.js'
import { FONT_FAMILY, SignaturePad } from './signature-pad.js'
import { OptionCards } from './option-cards.js'
import { PaymentsTimeline } from './payments-timeline.js'
import { VisitCard } from './visit-card.js'
import { ProposalTag } from './tag.js'
import { GlyphChat, GlyphCheckBold, GlyphInfo, GlyphLock, GlyphPlus } from './glyphs.js'

export const SECTION_IDS = {
  included: 'proposal-included',
  choice: 'proposal-choice',
  optional: 'proposal-optional',
  total: 'proposal-total',
  terms: 'proposal-terms',
  sign: 'proposal-sign',
} as const

export interface BodyProps {
  view: ProposalView
  selection: ProposalSelection
  onSelection: (selection: ProposalSelection) => void
  totals: SelectionTotals
  ready: boolean
  /**
   * True while `view.payments` still describe what is ticked.
   *
   * The server priced them for `view.initial`, so the moment somebody picks a
   * different option they describe a choice nobody made. Carried down rather
   * than recomputed per section, because the one place it is *wrong* to
   * recompute is the one place it matters.
   */
  priced: boolean
  /**
   * The same total said by the period, or `null` where there is no honest
   * "per month" (§4.4). Computed on the page from `monthSplit()` so it moves
   * the instant somebody taps, and agreed with by the server's `price` call.
   */
  month: MonthSplit | null
  /**
   * The payment rows for the selection on screen — the live ones once the
   * `price` call has answered, `view.payments` until then. Handed down rather
   * than read off the view, because the view's were priced for the *opening*
   * selection and nothing downstream can tell the difference.
   */
  payments: ProposalPayment[]
  /**
   * Which way the total is being read — **Total** or **Per month** — and the
   * one way to change it.
   *
   * Held by the page rather than by the total card, because the summary rail,
   * the bottom bar and the Included heading lead with whichever reading is
   * chosen (Aaron's review, item 2.5), and four components each remembering
   * the choice is four answers the first time one of them is wrong.
   */
  monthMode: MonthMode
  onMonthMode: (mode: MonthMode) => void
  /** The 390px layout, decided once by the page rather than per component. */
  phone: boolean
  /**
   * Below `RAIL_FROM` (1280px) — a phone or an iPad either way up — where the
   * summary rail has no room and its two lines are the bottom bar instead
   * (§6 item 11). The sign card follows the same line: below it the card is
   * a sheet the bar opens (§8.1).
   */
  narrow: boolean
}

// ---------------------------------------------------------------------------
// The work as offered
// ---------------------------------------------------------------------------

export function IncludedSection(props: BodyProps) {
  const { view, totals, month, ready, monthMode } = props
  const visits = view.visits.filter((visit) => visit.kind === 'included')
  if (visits.length === 0) return null
  const sum = visits.reduce((into, visit) => into + (totals.byVisit[visit.id] ?? 0), 0)
  const numbers = visitNumbers(view)

  /*
   * **By the month, the heading leads with the month** (Aaron's review, item
   * 2.5): "$950 / month" with what it comes to under it, read off the very
   * rows the Per month card lists for these visits. Only where the reader is
   * on Per month and some of this work is billed by the period — a one-off
   * installation keeps its total, because "$0 / month" over it is a reading
   * that does not apply.
   */
  const part = monthOffered(view, month, ready) && monthMode === 'period' && view.month && month
    ? splitOf(month, visits.map((visit) => visit.id))
    : null

  return (
    <section id={SECTION_IDS.included} className="scroll-mt-20">
      <SectionHead
        phone={props.phone}
        title="Included in your proposal"
        sub={`The work we are doing for you. ${partOfThePrice(visits.map((one) => numbers.get(one.id)!))}`}
        {...(part && view.month
          ? {
            figure: `${part.fromPeriod ? 'From ' : ''}${formatDollars(part.perPeriod)}`,
            unit: `/ ${view.month.label}`,
            figureLabel: part.oneTime > 0
              ? `+ ${formatDollarsExact(part.oneTime)} once · ${formatDollarsExact(part.inAll)} in all`
              : `${formatDollarsExact(part.inAll)} in all`,
          }
          : { figure: formatDollarsExact(sum), figureLabel: 'included' })}
      />
      <div className={cn("flex flex-col", props.phone ? "gap-3.5" : "gap-[22px]")}>
        {visits.map((visit, at) => (
          <VisitCard key={visit.id} {...props} visit={visit}
            index={numbers.get(visit.id) ?? null} openFirst={at === 0} />
        ))}
      </div>
    </section>
  )
}

/**
 * Every visit's number on the page — board 1: the included visits from 1,
 * then **one number per question** ("Visit 3" heads the renovation, whichever
 * answer is picked), then the optional extras. One counter for the whole
 * document, so "visit 4" in a question to the office means one thing.
 */
export function visitNumbers(view: ProposalView): Map<string, number> {
  const numbers = new Map<string, number>()
  let next = 1
  for (const visit of view.visits) {
    if (visit.kind === 'included') numbers.set(visit.id, next++)
  }
  for (const group of view.groups) numbers.set(group.id, next++)
  for (const visit of view.visits) {
    if (visit.kind === 'optional') numbers.set(visit.id, next++)
  }
  return numbers
}

/** "Visits 1 and 2 are part of the price." — board 1's sentence. */
function partOfThePrice(numbers: number[]): string {
  if (numbers.length === 1) return `Visit ${numbers[0]} is part of the price.`
  const head = numbers.slice(0, -1).join(', ')
  return `Visits ${head} and ${numbers[numbers.length - 1]} are part of the price.`
}

// ---------------------------------------------------------------------------
// Choose one
// ---------------------------------------------------------------------------

export function ChoiceSection(props: BodyProps) {
  const { view, selection, onSelection, phone, totals } = props
  if (view.groups.length === 0) return null
  const numbers = visitNumbers(view)

  /* What the picks add up to, beside the heading (board 1: "+$2,198 your pick"). */
  const picks = view.groups.map((group) => selection.choose[group.id]).filter(Boolean)
  const pickedVisits = view.visits.filter((visit) => visit.kind === 'choice'
    && visit.optionIds.some((id) => picks.includes(id)))
  const sum = pickedVisits.reduce((into, visit) => into + (totals.byVisit[visit.id] ?? 0), 0)
  const recommends = view.groups.some((group) => group.options.some((one) => one.recommended))
  const decisions = view.groups.length === 1 ? 'One decision to make.'
    : `${view.groups.length} decisions to make, one in each group.`

  return (
    <section id={SECTION_IDS.choice} className="scroll-mt-20">
      <SectionHead
        phone={phone}
        title={choiceHeading(view)}
        sub={recommends ? `${decisions} We have marked the one we would do.` : decisions}
        figure={picks.length > 0 ? `+${formatDollarsExact(sum)}` : undefined}
        figureLabel={view.groups.length === 1 ? 'your pick' : 'your picks'}
      />
      <div className={cn('flex flex-col', phone ? 'gap-8' : 'gap-10')}>
        {view.groups.map((group) => (
          <div key={group.id} className={cn('flex flex-col', phone ? 'gap-3.5' : 'gap-[18px]')}>
            <div className="flex flex-wrap items-center gap-3">
              <ProposalTag kind="choose" />
              <h3 className={cn('m-0 font-bold tracking-[-0.02em] text-text',
                phone ? 'text-[20px]' : 'text-[23px]')}>
                {group.name}
              </h3>
              {/* "Visit 3" names one trip; a plan of eight trips has no one number. */}
              {numbers.has(group.id) && group.options.every((one) => one.visitIds.length <= 1) && (
                <span className="text-[14px] text-text-muted">{`Visit ${numbers.get(group.id)}`}</span>
              )}
            </div>
            <OptionCards
              view={view}
              group={group}
              selection={selection}
              onSelection={onSelection}
              phone={phone}
            />
          </div>
        ))}
      </div>
    </section>
  )
}

/**
 * "Choose a plan" where an option is several trips, "Your choice" otherwise
 * (§6 item 4).
 *
 * The distinction is the customer's: picking between two ways of doing one job
 * is a choice, and picking between two seasons of service is a plan.
 */
export function choiceHeading(view: ProposalView): string {
  const several = view.groups.some((group) =>
    group.options.some((option) => option.visitIds.length > 1))
  return several ? 'Choose a plan' : 'Your choice'
}

// ---------------------------------------------------------------------------
// Optional extras
// ---------------------------------------------------------------------------

export function OptionalSection(props: BodyProps) {
  const { view, selection, totals } = props
  const extras = view.visits.filter((visit) => visit.kind === 'optional')
  if (extras.length === 0) return null
  const numbers = visitNumbers(view)
  const added = extras.filter((visit) => visitIncluded(visit, selection))
  const adds = added.reduce((into, visit) => into + (totals.byVisit[visit.id] ?? 0), 0)

  return (
    <section id={SECTION_IDS.optional} className="scroll-mt-20">
      <SectionHead
        phone={props.phone}
        title="Optional extras"
        sub="Not in the price. Switch on anything you would like and your total updates."
        figure={added.length === 0 ? 'None added' : `+${formatDollarsExact(adds)}`}
        figureLabel={added.length === 0 ? 'tap to add' : 'added'}
      />
      {/*
        * Each extra is its own card with its switch in the head (board 4):
        * the card itself changes — dashed and white, then tinted — and
        * nothing inside it moves.
        */}
      <div className={cn("flex flex-col", props.phone ? "gap-3.5" : "gap-[22px]")}>
        {extras.map((visit) => (
          <VisitCard key={visit.id} {...props} visit={visit}
            index={numbers.get(visit.id) ?? null} />
        ))}
      </div>
    </section>
  )
}

// ---------------------------------------------------------------------------
// Your total
// ---------------------------------------------------------------------------

export function TotalSection(props: BodyProps) {
  const {
    view, totals, ready, priced, month, payments, phone, selection, onSelection,
    monthMode, onMonthMode,
  } = props

  /*
   * The toggle exists only where the quote really is billed by the period
   * (`view.month`, decided on the server) **and** the selection on screen
   * actually puts something on such a plan — `monthOffered`, the one test
   * the rail and the bar ask too.
   */
  const cadence = view.month
  const byPeriod = monthOffered(view, month, ready)
  const showMonths = byPeriod && monthMode === 'period'
  const rows = addingUp(view, selection, totals)

  return (
    <section id={SECTION_IDS.total} className="scroll-mt-20">
      <SectionHead phone={phone} title="Your total"
        sub={view.plans.length > 0
          ? 'Everything above, added up, and when each payment is due.'
          : 'Everything above, added up.'} />

      {/* Board 2: "Adding it up" — a receipt of the choices, to the cent. */}
      <div aria-live="polite"
        className={cn('rounded-[18px] border border-line bg-surface shadow-proposal',
          phone ? 'px-4 pb-5 pt-5' : 'px-8 pb-7 pt-[30px]')}>
        <div className="flex items-center justify-between gap-3 pb-3.5">
          <b className="text-[18px] tracking-[-0.01em] text-text">Adding it up</b>
          {byPeriod && cadence && <MonthToggle month={cadence} mode={monthMode} onMode={onMonthMode} />}
        </div>

        {showMonths && cadence && month
          ? <MonthBreakdown view={view} month={cadence} split={month} phone={phone} />
          : (
            <>
              {rows.map((row) => row.added
                ? (
                  <div key={row.key}
                    className="flex items-start justify-between gap-3.5 border-t border-line-soft py-[13px]">
                    <div className="flex min-w-0 flex-col gap-0.5">
                      <span className="flex flex-wrap items-center gap-2 text-[15.5px] font-semibold text-text">
                        {row.name}
                        {row.times && (
                          <span className="tnum inline-flex h-[22px] items-center rounded-[7px] bg-brand-t2
                                           px-2 text-[12px] font-bold text-brand-tx">
                            {`× ${row.times}`}
                          </span>
                        )}
                      </span>
                      {row.sub && <span className="tnum text-[13px] text-text-muted">{row.sub}</span>}
                    </div>
                    <span className="tnum shrink-0 text-[15.5px] font-[650] text-text">
                      {row.amount === null ? '—' : cents(row.amount)}
                    </span>
                  </div>
                  )
                : (
                  /*
                   * An optional extra nobody switched on stays on the receipt,
                   * dashed and grey, with the one tap that adds it — board 2.
                   */
                  <div key={row.key}
                    className="flex items-center justify-between gap-3.5 border-t border-dashed
                               border-line py-[13px] text-text-muted">
                    <div className="flex min-w-0 items-center gap-2.5">
                      <span className="flex h-[22px] w-[22px] shrink-0 items-center justify-center
                                       rounded-full border-[1.5px] border-dashed border-text-faint text-text-faint">
                        <GlyphPlus size={12} />
                      </span>
                      <span className="min-w-0 text-[14.5px]">
                        {row.name}
                        <span className="text-text-faint"> · not added</span>
                      </span>
                    </div>
                    <button type="button"
                      onClick={() => onSelection({ ...selection, visits: [...selection.visits, row.key] })}
                      className="tnum -my-3 min-h-[44px] shrink-0 text-[14px] font-semibold text-brand-tx">
                      {`Add +${formatDollarsExact(row.amount ?? 0)}`}
                    </button>
                  </div>
                  ))}

              {ready && view.tax.rate > 0 && (
                <>
                  <div className="flex justify-between pb-0.5 pt-2.5 text-[14.5px] text-text-body">
                    <span>Subtotal</span>
                    <span className="tnum font-semibold">{cents(totals.subtotal)}</span>
                  </div>
                  {/*
                    * Tax is an estimate, said as one, and only where the company
                    * has told us its rate: a "$0" beside a signature is a figure
                    * somebody acts on, where a missing line is a question (§1.4).
                    */}
                  <div className="flex items-start justify-between pb-3 pt-0.5 text-[14.5px] text-text-body">
                    <span className="flex flex-col gap-px">
                      Sales tax (estimated)
                      <span className="text-[12.5px] text-text-faint">
                        {[view.tax.label, 'Confirmed on each invoice.'].filter(Boolean).join('. ')}
                      </span>
                    </span>
                    <span className="tnum font-semibold">{cents(totals.tax)}</span>
                  </div>
                </>
              )}
            </>
            )}

        {/*
          * By the month, the tinted summary box is the last thing above the
          * rule, and the two sat touching — a box standing on a 2px line
          * reads as one object, so "Monthly $950" looked like part of "Your
          * total" (Aaron's review, item 2.3). Board 13 cell 2 draws the gap.
          */}
        <div className={cn('flex items-baseline justify-between border-t-2 border-text pt-4',
          showMonths ? 'mt-5' : !(ready && view.tax.rate > 0) && 'mt-2')}>
          <b className="text-[17px] text-text">Your total</b>
          {/*
            * A dash until every question has an answer: nothing is
            * preselected in a choose-one group unless somebody marked an
            * answer Recommended (§7.1), and the sum of nothing is not "$0".
            */}
          <b className={cn('tnum tracking-[-0.025em] text-text', phone ? 'text-[30px]' : 'text-[36px]')}>
            {ready ? cents(totals.total) : '—'}
          </b>
        </div>
        {!ready && (
          <p className="m-0 mt-1 text-[13.5px] text-text-muted">
            Choose an option above to see your total.
          </p>
        )}
      </div>

      <Payments view={view} payments={payments} priced={priced} phone={phone} />
    </section>
  )
}

/** Board 2's figures carry their cents: "$864.00", "$3,527.72". */
const cents = (amount: number): string => new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2,
}).format(amount)

interface AddingRow {
  key: string
  name: string
  times: number | null
  sub: string | null
  amount: number | null
  added: boolean
}

/**
 * The receipt's rows, in the page's own order: the included visits, one row
 * per question naming the answer ("Front bed renovation · Premium"), then
 * the optional extras — added, or dashed with an Add.
 */
function addingUp(
  view: ProposalView, selection: ProposalSelection, totals: SelectionTotals,
): AddingRow[] {
  const rows: AddingRow[] = []
  for (const visit of view.visits) {
    if (visit.kind !== 'included') continue
    rows.push({
      key: visit.id,
      name: visit.name,
      times: (visit.count.n ?? 1) > 1 ? visit.count.n : null,
      sub: visit.price.plan.unit && !visit.count.openEnded ? priceLine(visit).replace(' a ', ' / ') : null,
      amount: totals.byVisit[visit.id] ?? 0,
      added: true,
    })
  }
  for (const group of view.groups) {
    const picked = selection.choose[group.id]
    const option = group.options.find((one) => one.id === picked)
    if (picked === null && group.allowNone) continue
    rows.push({
      key: group.id,
      name: option ? `${group.name} · ${option.name}` : group.name,
      times: null,
      sub: option ? null : 'Not chosen yet',
      amount: option
        ? option.visitIds.reduce((into, id) => into + (totals.byVisit[id] ?? 0), 0)
        : null,
      added: true,
    })
  }
  for (const visit of view.visits) {
    if (visit.kind !== 'optional') continue
    const on = visitIncluded(visit, selection)
    rows.push({
      key: visit.id,
      name: visit.name,
      times: on && (visit.count.n ?? 1) > 1 ? visit.count.n : null,
      sub: null,
      amount: on ? totals.byVisit[visit.id] ?? 0 : visit.price.total,
      added: on,
    })
  }
  return rows
}

/**
 * How and when it will be billed, in the customer's words.
 *
 * "We will invoice $6,592 when you approve and the balance when the work is
 * finished" is the single most asked question about any quote, and leaving it
 * to a phone call is how a deposit becomes a surprise.
 *
 * The rows follow the **selection** — priced on the server for the visits the
 * customer has actually taken, so a payment for an alternative nobody chose is
 * not listed (§1.5, §4.4) — and they carry no working date: a day on this list
 * comes from the billing plan itself or not at all (rule 14), so an unfixed
 * row reads "Month 2" rather than naming a day nobody committed to.
 *
 * Its own card under the total (board 2): this component is only the
 * decision of *whether there is a schedule to print*; `PaymentsTimeline`
 * draws it, and `foldPayments` in `shared` decides what folds.
 */
function Payments({ view, payments, priced, phone }: {
  view: ProposalView
  payments: ProposalPayment[]
  priced: boolean
  phone: boolean
}) {
  if (view.plans.length === 0) return null

  return (
    <div className={cn('mt-[22px] rounded-[18px] border border-line bg-surface shadow-proposal',
      phone ? 'px-4 pb-5 pt-5' : 'px-[30px] pb-[26px] pt-[30px]')}>
      {/*
        * The question is asked whenever the quote bills at all, and answered
        * with whatever is true: while the rows on hand describe a choice
        * nobody made, the sentence replaces them rather than the page printing
        * the wrong schedule.
        */}
      {!priced || payments.length === 0
        ? (
          <>
            <b className="text-[18px] tracking-[-0.01em] text-text">When you pay</b>
            <p className="m-0 mt-2 text-[14px] leading-[1.5] text-text-muted">
              We will confirm the payment schedule for the choices you have made before
              anything is invoiced.
            </p>
          </>
          )
        : <PaymentsTimeline view={view} payments={payments} phone={phone} />}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Sign
// ---------------------------------------------------------------------------

export interface SignProps extends BodyProps {
  /** The whole signature. `agreed` is the checkbox and is enforced here. */
  onApprove: (signature: SignaturePayload) => Promise<void>
  busy?: boolean
  error?: string | null
  footnote?: string
  /**
   * The other three things somebody does with a proposal (§9.4).
   *
   * Absent on the office preview and on the quote builder's Proposal tab,
   * where there is nobody to ask — and the links are then not drawn at all
   * rather than drawn and inert.
   */
  onRespond?: (kind: 'question' | 'changes' | 'decline') => void
}

/**
 * **Ready? Sign to accept** — `docs/proposal.md` §8.1, board 7.
 *
 * The last card on the page, and the only one that asks for something. In
 * order: a recap of what is included *said the way each visit bills*, a note
 * about anything optional that was left off, the name, Type or Draw, the
 * agreement row, and the button — which is **grey rather than hidden** until
 * everything is there, beside a list of exactly what is still missing.
 *
 * `missingForAccept` in `shared` decides all of that, and the server calls the
 * same function. A page that offers Accept and a server that refuses it is
 * the way this goes wrong: somebody signs, waits, and is told something they
 * could not see.
 */
export function SignSection(props: SignProps) {
  const { phone, narrow } = props
  /*
   * **On a phone and an iPad the card is a sheet** (§8.1's last line, boards
   * 8 and 9), reached from the sticky bar's **Review and sign** — because the
   * bar is what is on the screen when somebody has decided, and scrolling
   * them to the bottom of a four-screen document to find the button is the
   * thing the bar exists to avoid. On a desktop it is the last card in the
   * column and the bar is not drawn at all.
   *
   * `SheetOrDialog` is the switch and **the body is the same component
   * either way**: a second copy of a signature card is a second place for the
   * agreement row to be forgotten.
   */
  const [open, setOpen] = useState(false)

  if (narrow) {
    return (
      <>
        <section id={SECTION_IDS.sign} className="scroll-mt-20">
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="flex h-14 w-full items-center justify-center rounded-touch bg-brand
                       text-[16px] font-semibold text-brand-on"
          >
            Review and sign
          </button>
        </section>
        {open && (
          <SheetOrDialog
            /* Boards 8 and 9: the sheet names the document, not the question. */
            title="Sign to accept"
            sub={phone
              ? [props.view.preparedFor, props.view.reference].filter(Boolean).join(' · ')
              : `Proposal ${props.view.reference} · ${props.view.identity.name}`}
            width={680}
            phone={phone}
            tall
            onClose={() => setOpen(false)}
          >
            <div className="px-4 pb-4 pt-1">
              <SignBody {...props} bare />
            </div>
          </SheetOrDialog>
        )}
      </>
    )
  }

  return (
    <section id={SECTION_IDS.sign} className="scroll-mt-20">
      <SignBody {...props} />
    </section>
  )
}

/**
 * The card's own contents, in the column or in the sheet.
 *
 * `bare` drops the frame and the heading, which the sheet's own header
 * already carries — everything else, including which things are still
 * missing, is the same component in both.
 */
function SignBody(props: SignProps & { bare?: boolean }) {
  const { view, selection, onSelection, totals, phone, bare } = props
  const { onApprove, busy, error, footnote } = props

  const [form, setForm] = useState<SignatureForm>(EMPTY_SIGNATURE)
  const [png, setPng] = useState<string | null>(null)
  /** Shown only after a press, so the list is an answer rather than a nag. */
  const [asked, setAsked] = useState(false)

  const missing = missingForAccept(view, selection, form)
  const canAccept = missing.length === 0 && !busy

  const included = view.visits.filter((visit) => visitIncluded(visit, selection))
  const left = view.visits.filter((visit) =>
    visit.kind === 'optional' && !selection.visits.includes(visit.id))

  const accept = () => {
    if (!canAccept) { setAsked(true); return }
    const name = form.name.trim()
    void onApprove(form.mode === 'drawn' && png
      ? { kind: 'drawn', name, png }
      : { kind: 'typed', name, font: form.font })
  }

  return (
    <div className={cn('flex flex-col gap-5',
      !bare && 'rounded-[18px] border border-line bg-surface shadow-proposal',
      !bare && (phone ? 'px-4 py-6' : 'px-9 py-[34px]'))}>
      {/* In a sheet the title bar already says it (boards 8 and 9). */}
      {!bare && (
        <div className="flex flex-col gap-1.5">
          <h2 className={cn('m-0 font-bold tracking-[-0.02em] text-text',
            phone ? 'text-[24px]' : 'text-[28px]')}>
            Ready? Sign to accept
          </h2>
          {/*
            * Board 7's sentence: what to do, and that the company will be in
            * touch — no date, no process, nothing it did not write (rule 12).
            */}
          <span className="text-[15.5px] leading-[1.5] text-text-body">
            {/*
              * Board 7 adds "{Company} will be in touch about what happens
              * next". §8.1 overrules it on purpose: what happens next is the
              * company's own wording, on the thank-you, from `nextSteps`.
              */}
            {footnote ?? 'Check your choices below, then sign to accept.'}
          </span>
        </div>
      )}

      <Recap visits={included} totals={totals} chosen={chosenOptions(view, selection)} />

      {left.length > 0 && (
        <NotAdded
          left={left}
          onAdd={(id) => onSelection({ ...selection, visits: [...selection.visits, id] })}
        />
      )}

      <label className="flex flex-col gap-[7px]">
        <span className="text-[13.5px] font-[650] text-text">Your full name</span>
        <input
          value={form.name}
          autoComplete="name"
          placeholder="Type your full name"
          disabled={busy}
          onChange={(event) => setForm({ ...form, name: event.target.value })}
          /*
           * 50px and 16px type: anything under 16 makes iOS zoom the page on
           * focus, which on the one field somebody has to fill in is a layout
           * shift mid-thought (§8.1 item 4). Board 7 draws the focus as the
           * brand border with a 4px ring.
           */
          className="h-[50px] w-full rounded-[12px] border-[1.6px] border-line bg-surface px-4
                     text-[16px] text-text placeholder:text-text-faint focus:border-brand-ui
                     focus:shadow-[0_0_0_4px_var(--ring)] focus:outline-none"
        />
      </label>

      <SignaturePad form={form} onForm={setForm} onPng={setPng} disabled={busy} />

      {/* The row is the target, not the box (§13): a 26px brand box in a tinted row. */}
      <label className={cn('flex cursor-pointer items-start gap-3.5 rounded-[14px] border p-4',
        form.agreed ? 'border-brand-ring bg-brand-t1' : 'border-line-soft bg-bg')}>
        <input
          type="checkbox"
          className="peer sr-only"
          checked={form.agreed}
          disabled={busy}
          onChange={(event) => setForm({ ...form, agreed: event.target.checked })}
        />
        <span aria-hidden="true"
          className={cn('flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-[7px]',
            'peer-focus-visible:ring-2 peer-focus-visible:ring-brand-ring',
            form.agreed ? 'bg-brand-ui text-brand-on' : 'border-[1.6px] border-text-faint bg-surface')}>
          {form.agreed && <GlyphCheckBold size={18} />}
        </span>
        <span className="text-[15px] leading-[1.5] text-text">
          {view.terms && (
            <>
              I have read and agree to the{' '}
              <button
                type="button"
                className="font-semibold text-brand-tx"
                onClick={(event) => {
                  event.preventDefault()
                  document.getElementById(SECTION_IDS.terms)
                    ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }}
              >
                terms of service
              </button>
              .{' '}
            </>
          )}
          I authorize {view.identity.name} to do the work described in this proposal,
          billed as shown.
        </span>
      </label>

      {/*
        * **Never hidden and never only "Disabled"** (board 7 cell 3). A grey
        * button with no reason beside it is the page refusing and not saying
        * why; this is `missingForAccept` rendered, in its own order.
        */}
      {missing.length > 0 && asked && (
        <div className="flex flex-col gap-2 rounded-[12px] bg-bg px-4 py-3.5">
          <b className="text-[13.5px] text-text">To accept, you still need to</b>
          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {missing.map((item) => (
              <li key={`${item.key}:${item.groupId ?? ''}`}
                className="flex items-center gap-2.5 text-[14px] text-text-body">
                <span aria-hidden="true"
                  className="h-[18px] w-[18px] shrink-0 rounded-full border-[1.6px] border-text-faint" />
                {item.text}
              </li>
            ))}
          </ul>
        </div>
      )}

      {error && <p className="m-0 text-[13.5px] text-danger">{error}</p>}

      <button
        type="button"
        onClick={accept}
        aria-disabled={!canAccept}
        className={cn('flex w-full items-center justify-center gap-2 rounded-[14px] font-semibold',
          phone ? 'h-14 text-[16px]' : 'h-[60px] text-[17px]',
          canAccept ? 'bg-brand text-brand-on' : 'bg-line-soft text-text-faint')}
      >
        <GlyphCheckBold size={20} />
        {busy ? 'Accepting…' : `Accept and sign · ${formatDollarsExact(totals.total)}`}
      </button>

      {/*
        * **Not every answer is yes** (§9.4, board 7). Three links, one tap
        * each, under the one button the card recommends — a page that offers
        * only Accept is one people answer by not answering at all.
        */}
      {props.onRespond && <ResponseLinks onOpen={props.onRespond} />}

      <p className="m-0 flex justify-center gap-2.5 text-center text-[12.5px] leading-[1.5] text-text-faint">
        <GlyphLock size={14} className="mt-0.5 shrink-0" />
        <span>
          By signing you agree to sign electronically. We record the time, your device and
          exactly what you chose.
        </span>
      </p>
    </div>
  )
}

/** Which option each group ended up on, for the recap's own line. */
function chosenOptions(view: ProposalView, sel: ProposalSelection) {
  return view.groups.flatMap((group) => {
    const picked = sel.choose[group.id]
    const option = picked ? group.options.find((one) => one.id === picked) : undefined
    return option
      ? [{ group: group.name, groupId: group.id, option: option.name, visitIds: option.visitIds }]
      : []
  })
}

/**
 * What is included, and what it comes to — the last thing read before signing.
 *
 * Each row's price is `priceLine()` off the **same `ProposalPrice`** the visit
 * card drew, so the recap cannot quote a visit differently from the page above
 * it. Board 7 carries no monthly line here: the rail and the phone bar say it,
 * and beside a signature the one figure is the total.
 */
function Recap({ visits, totals, chosen }: {
  visits: ProposalVisit[]
  totals: SelectionTotals
  chosen: { group: string; groupId: string; option: string; visitIds: string[] }[]
}) {
  /* Board 7: a visit that answers a question is listed as "Question, answer". */
  const inChosen = new Set(chosen.flatMap((one) => one.visitIds))
  const rows = [
    ...visits.filter((visit) => !inChosen.has(visit.id)).map((visit) => ({
      key: visit.id,
      name: visit.name,
      count: !visit.count.openEnded && (visit.count.n ?? 0) > 1
        ? `${visit.count.n} ${visit.count.unit}` : null,
      amount: visit.count.openEnded && visit.recurring
        ? `${formatDollarsExact(visit.recurring.amount)} / ${visit.recurring.label}`
        : recapCents(totals.byVisit[visit.id] ?? 0),
    })),
    ...chosen.map((one) => ({
      key: one.groupId,
      name: `${one.group}, ${one.option}`,
      count: null,
      amount: recapCents(one.visitIds.reduce((into, id) => into + (totals.byVisit[id] ?? 0), 0)),
    })),
  ]

  return (
    <div className="flex flex-col gap-3 rounded-[14px] border border-line-soft bg-surface-alt px-[18px] py-4">
      <div className="flex flex-col gap-[9px]">
        {rows.map((row) => (
          <div key={row.key} className="flex items-center gap-2.5 text-[14.5px]">
            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand-t2
                             text-brand-tx">
              <GlyphCheckBold size={12} />
            </span>
            <span className="min-w-0 flex-1 text-text">
              {row.name}
              {row.count && <span className="tnum text-text-faint">{` · ${row.count}`}</span>}
            </span>
            <span className="tnum shrink-0 font-semibold text-text">{row.amount}</span>
          </div>
        ))}
      </div>
      <div className="flex items-baseline justify-between border-t border-line pt-3">
        <b className="text-[15.5px] text-text">Total, with tax</b>
        <b className="tnum text-[22px] tracking-[-0.02em] text-text">{recapCents(totals.total)}</b>
      </div>
    </div>
  )
}

const recapCents = (amount: number): string => new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2,
}).format(amount)

/**
 * "You have not added Irrigation winterization (+$145)."
 *
 * One note for the first thing left off, because a list of everything
 * unticked is the Optional section again; "and 2 more" says there is a list
 * without being one. The amount is said in the unit of the thing it joins —
 * "+$40 / month" on a visit that bills by the period — which is the same rule
 * the optional row itself follows (§6.3).
 */
function NotAdded({ left, onAdd }: {
  left: ProposalVisit[]
  onAdd: (visitId: string) => void
}) {
  const first = left[0]!
  const more = left.length - 1

  /* Board 7: the amber band, an info glyph, and Add as the last word. */
  return (
    <div className="flex items-center gap-3 rounded-[12px] border border-amber-band-line bg-amber-band-bg
                    px-3.5 py-3 text-[14px] leading-[1.4] text-amber-band-text">
      <GlyphInfo size={18} className="shrink-0" />
      <span className="min-w-0 flex-1">
        You have not added <b>{first.name}</b>
        {` (+${priceLine(first)})`}
        {more > 0 && `, and ${more} more`}. You can still add{more > 0 ? ' them' : ' it'} before
        you sign.
      </span>
      <button
        type="button"
        onClick={() => onAdd(first.id)}
        className="-my-3 min-h-[44px] shrink-0 whitespace-nowrap font-bold"
      >
        Add
      </button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// The signed copy (§8.4's last paragraph, §9.3)
// ---------------------------------------------------------------------------

/**
 * What the page becomes once somebody has signed it.
 *
 * **It is read off the signature, not off the quote** (`view.signed`, which
 * the server builds from the append-only `quote_approvals` row). The quote
 * goes on moving — a change order, a next version, an edited catalog — and a
 * copy projected from it would quietly rewrite what was agreed to. So this
 * shows the figures that were printed beside the signature line, the visits
 * that were taken, and the signature itself.
 *
 * The Accept card is gone, because there is nothing left to accept, and the
 * terms below open **every** section under a stamp saying what day they stood
 * on.
 */
export function SignedSection({ view, phone, onMessage }: {
  view: ProposalView
  phone: boolean
  /** "Message Alex" — the question dialog, where the page has one. */
  onMessage?: () => void
}) {
  /*
   * Board 11 names a chosen visit with its question — "Front bed renovation,
   * Premium" — because "Premium" alone, months later, says nothing about what
   * it was the premium version of.
   */
  const answers = view.signed?.chosenOptions ?? []
  const answerOf = new Map(answers.map((one) =>
    [one.optionName, `${one.groupName}, ${one.optionName}`]))
  const recapNames = new Set((view.signed?.recap ?? []).map((row) => row.name))
  const unnamedAnswers = answers.filter((one) => !recapNames.has(one.optionName))
  const signed = view.signed
  if (!signed) return null
  const who = view.estimator?.name.trim().split(/\s+/)[0] ?? null
  const at = new Date(signed.approvedAt)
  const day = at.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  const time = at.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })

  return (
    <section id={SECTION_IDS.sign} className="flex scroll-mt-20 flex-col gap-3.5">
      {/* Board 11 cell 2: what happened, and the signature as it was made. */}
      <div className="flex flex-col gap-3.5 rounded-[18px] border border-brand-ring bg-brand-t1 p-[18px]
                      shadow-proposal">
        <div className="flex items-start gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand text-brand-on">
            <GlyphCheckBold size={18} />
          </span>
          <span className="flex flex-col gap-0.5">
            <b className="text-[16px] text-text">{`You accepted this on ${day}`}</b>
            <span className="text-[13.5px] text-text-muted">
              Nothing more to do. This is your signed copy.
            </span>
          </span>
        </div>
        <div className="rounded-[14px] border border-line-soft bg-surface-alt px-4 pb-3 pt-4">
          <div className="flex items-end gap-3 border-b border-line-strong pb-1">
            <span className={cn('flex min-w-0 flex-1 items-end overflow-hidden',
              phone ? 'min-h-[48px]' : 'min-h-[56px]')}>
              {signed.kind === 'drawn' && signed.imageUrl
                ? <img src={signed.imageUrl} alt={`${signed.name}'s signature`} className="max-h-[96px] w-auto" />
                : (
                  <span className="truncate text-[30px] leading-[1.15] text-text"
                    style={signed.font ? { fontFamily: FONT_FAMILY[signed.font] } : undefined}>
                    {signed.name}
                  </span>
                  )}
            </span>
            <GlyphLock size={16} className="mb-2 shrink-0 text-text-faint" />
          </div>
          <span className="mt-2 block text-[12.5px] text-text-faint">
            {`Signed by ${signed.name} · ${formatDocumentDate(signed.approvedAt)} · ${time}`}
          </span>
        </div>
      </div>

      {/* What was taken, locked — the figures printed beside the signature line. */}
      <div className="flex flex-col gap-3 rounded-[18px] border border-line bg-surface px-[22px] py-5 shadow-proposal">
        <div className="flex items-baseline justify-between">
          <b className="text-[17px] text-text">What you chose</b>
          <span className="text-[12.5px] text-text-faint">Locked</span>
        </div>
        <div className="flex flex-col gap-[9px]">
          {/*
            * A plan whose visits are named otherwise ("Silver" holding
            * "Spring feed" and "Fall feed") is said once, above them, so the
            * answer to every question is on the copy that was signed.
            */}
          {unnamedAnswers.map((answer) => (
            <div key={`${answer.groupName}:${answer.optionName}`}
              className="flex items-center gap-2.5 text-[14.5px]">
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand-t2
                               text-brand-tx">
                <GlyphCheckBold size={12} />
              </span>
              <span className="min-w-0 flex-1 text-text">
                {`${answer.groupName}, ${answer.optionName}`}
              </span>
            </div>
          ))}
          {signed.recap.map((row) => (
            <div key={row.name} className="flex items-center gap-2.5 text-[14.5px]">
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand-t2
                               text-brand-tx">
                <GlyphCheckBold size={12} />
              </span>
              <span className="min-w-0 flex-1 text-text">
                {answerOf.get(row.name) ?? row.name}
                {row.count && row.count !== '1 visit' && (
                  <span className="tnum text-text-faint">{` · ${row.count}`}</span>
                )}
              </span>
              <span className="tnum shrink-0 font-semibold text-text">{recapCents(row.total)}</span>
            </div>
          ))}
        </div>
        <div className="flex items-baseline justify-between border-t border-line pt-3">
          <b className="text-[15.5px] text-text">{view.tax.rate > 0 ? 'Total, with tax' : 'Total'}</b>
          <b className="tnum text-[26px] tracking-[-0.02em] text-text">{recapCents(signed.total)}</b>
        </div>
      </div>

      {/*
        * What was on offer and not taken. "We never offered that" and "you did
        * not take it" are different conversations, and only one of them can be
        * had from a document that lists both.
        */}
      {signed.notChosen.length > 0 && (
        <p className="m-0 flex gap-2.5 rounded-[18px] border border-line bg-surface px-[18px] py-3.5
                      text-[13.5px] leading-[1.5] text-text-muted">
          <GlyphInfo size={16} className="mt-0.5 shrink-0 text-text-faint" />
          <span>
            {`Not chosen: ${listWords(signed.notChosen.map((one) => one.name))}.`}
            {who && ` Ask ${who} if you would like to add something.`}
          </span>
        </p>
      )}

      <div className="flex flex-col gap-2.5">
        {signed.deposit?.payUrl && (
          <a href={signed.deposit.payUrl}
            className="flex h-[54px] w-full items-center justify-center rounded-[14px] bg-brand text-[16px]
                       font-semibold text-brand-on">
            {`Pay deposit · ${recapCents(signed.deposit.amount)}`}
          </a>
        )}
        {onMessage && who && (
          <button type="button" onClick={onMessage}
            className="flex h-[50px] w-full items-center justify-center gap-2 rounded-[14px] border border-line
                       bg-surface text-[14.5px] font-semibold text-text">
            <GlyphChat size={18} />
            {`Message ${who}`}
          </button>
        )}
      </div>
    </section>
  )
}

/** "Classic front bed, and Irrigation winterization" — board 11's list. */
const listWords = (names: string[]): string =>
  names.length <= 1 ? names.join('')
    : `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`

/** "these are the terms exactly as they stood when Dana signed. Version 3." */
export function SignedTermsStamp({ signed }: { signed: SignedSummary }) {
  const at = new Date(signed.approvedAt)
  const when = `${formatDocumentDate(signed.approvedAt)} · ${at.toLocaleTimeString('en-US',
    { hour: 'numeric', minute: '2-digit' })}`
  /* Board 16 cell 5: a tinted stamp with a lock, above the open terms. */
  return (
    <div className="flex items-start gap-3 rounded-[14px] border border-brand-ring bg-brand-t1 px-4 py-3">
      <GlyphLock size={16} className="mt-0.5 shrink-0 text-text-muted" />
      <span className="flex flex-col gap-px leading-[1.4]">
        <b className="text-[13.5px] text-text">{`Accepted ${when} by ${signed.name}`}</b>
        <span className="text-[13px] text-text-body">
          {`These are the terms exactly as they stood when ${firstWord(signed.name)} signed.`}
          {signed.termsRevision !== null && ` Version ${signed.termsRevision}.`}
        </span>
      </span>
    </div>
  )
}

const firstWord = (name: string): string => name.trim().split(/\s+/)[0] ?? name

// ---------------------------------------------------------------------------
// Shared pieces
// ---------------------------------------------------------------------------

function SectionHead({ title, sub, figure, unit, figureLabel, phone }: {
  title: string
  sub: string
  /** The right-hand figure: "$1,260", "+$2,198", "None added", "$950". */
  figure?: string
  /** Set apart beside the figure, as board 12 says a price: "/ month". */
  unit?: string
  figureLabel?: string
  phone: boolean
}) {
  const heading = (
    <h2 className={cn('m-0 font-bold tracking-[-0.02em] text-text',
      phone ? 'text-[22px]' : 'text-[26px]')}>
      {title}
    </h2>
  )
  const words = (
    <span className={cn('leading-[1.45] text-text-muted', phone ? 'text-[14px]' : 'text-[15px]')}>
      {sub}
    </span>
  )
  const amount = figure !== undefined && (
    <div className="shrink-0 text-right">
      <b className="tnum whitespace-nowrap text-[20px] font-bold tracking-[-0.01em] text-text">
        {figure}
        {unit && (
          <span className="ml-1 text-[13px] font-semibold tracking-normal text-text-body">{unit}</span>
        )}
      </b>
      {figureLabel && <div className="tnum text-[12.5px] text-text-faint">{figureLabel}</div>}
    </div>
  )

  /*
   * **On a phone the heading has the whole line** (Aaron's review, item
   * 2.6): beside a figure, "Included in your proposal" had about two thirds
   * of 358px and broke after "your", so the one line that names the section
   * read as two. The figure goes under it, at the right of the sentence,
   * where it still lines up with the money on the cards below.
   */
  if (phone) {
    return (
      <div className="mb-3.5 flex flex-col gap-1">
        {heading}
        <div className="flex items-end justify-between gap-4">
          <div className="min-w-0">{words}</div>
          {amount}
        </div>
      </div>
    )
  }

  return (
    <div className="mb-[18px] flex items-end justify-between gap-4">
      <div className="flex min-w-0 flex-col gap-1">
        {heading}
        {words}
      </div>
      {amount}
    </div>
  )
}
